import { createSlice } from '@reduxjs/toolkit';
import { prospectDocument, prospectDocumentStatus, prospectStates, prospectSubstates } from '@repo/backoffice-api';

export interface ProspectDataModel {
  encodedKey: string;
  prospectId: string;
  legalName: string;
  state: prospectStates;
  documentData: DocumentDataModel;
  allDocuments: prospectDocument[];
  substate: prospectSubstates | null;
  availableSubstates?: prospectSubstates[];
  patchStatus?: prospectSubstates;
  territoryManagerId?: number;
  legalRepresentativeEmail?: string;
  aiDataToView?: prospectDocument['inferenceResult'];
}

export interface DocumentDataModel {
  id: string;
  status: prospectDocumentStatus;
  storageKey?: string;
  title?: string;
  comment?: string | undefined;
}

const initialState: ProspectDataModel = {
  encodedKey: '',
  prospectId: '',
  legalName: '',
  state: 'IN_REVIEW',
  allDocuments: [],
  documentData: {
    id: '',
    storageKey: undefined,
    status: 'PENDING',
    title: undefined,
    comment: undefined,
  },
  substate: null,
  availableSubstates: [],
  patchStatus: undefined,
  territoryManagerId: undefined,
  legalRepresentativeEmail: undefined,
  aiDataToView: undefined,
};

export const ProspectDataSlice = createSlice({
  name: 'userdata',
  initialState,
  reducers: {
    pushProspectData: (state, action: { payload: Partial<ProspectDataModel> }) => ({
      ...state,
      ...action.payload,
    }),
    pushProspectId: (state, action: { payload: string }) => ({
      ...state,
      prospectId: action.payload,
    }),
    pushProspectPatchStatus: (state, action: { payload: prospectSubstates }) => ({
      ...state,
      patchStatus: action.payload,
    }),
    pushProspectDocuments: (state, action: { payload: prospectDocument[] }) => ({
      ...state,
      allDocuments: [...action.payload],
    }),
    pushDocumentData: (state, action: { payload: DocumentDataModel }) => ({
      ...state,
      documentData: { ...action.payload },
    }),
    resetDocumentData: (state) => ({
      ...state,
      documentData: initialState.documentData,
    }),
    refreshDocumentsAfterUpload: (state, action: { payload: prospectDocument }) => {
      const updatedDocs = state.allDocuments.map((doc) =>
        doc.documentType === action.payload.documentType ? action.payload : doc,
      );
      return { ...state, allDocuments: updatedDocs };
    },
    refreshDocumentsAfterDelete: (
      state,
      action: { payload: { documentType: string; title: string; required?: boolean } },
    ) => {
      const { documentType, title, required } = action.payload;
      const updatedDocs = state.allDocuments.map((doc) =>
        doc.documentType === documentType ? { documentType, title, required } : doc,
      );
      return { ...state, allDocuments: updatedDocs };
    },
    pushAIDataToView: (state, action: { payload: prospectDocument['inferenceResult'] }) => ({
      ...state,
      aiDataToView: action.payload,
    }),
    resetAIDataToView: (state) => ({
      ...state,
      aiDataToView: undefined,
    }),
    resetProspectData: () => initialState,
  },
});

export const {
  pushProspectId,
  pushProspectPatchStatus,
  pushDocumentData,
  pushProspectDocuments,
  resetDocumentData,
  resetProspectData,
  pushProspectData,
  refreshDocumentsAfterUpload,
  refreshDocumentsAfterDelete,
  pushAIDataToView,
  resetAIDataToView,
} = ProspectDataSlice.actions;

export default ProspectDataSlice.reducer;
